/**
 * Blocking update gate — shown when the native client is below the hard floor
 * (`force_update_below_ios` / `force_update_below_android`).
 *
 * Renders INSTEAD of the router (see App/index.tsx), so there is nothing behind
 * it and no route around it.
 *
 * PLATFORM ASYMMETRY — the reason this component looks the way it does:
 *
 *   Android has a real native forced-update flow. Google Play's IMMEDIATE
 *   in-app update is a full-screen, Play-rendered experience that downloads,
 *   installs, and restarts the app without the user ever leaving it. We fire it
 *   on mount. It is strictly better than anything we would draw.
 *
 *   iOS has no equivalent. Apple ships no API to prompt or force an update.
 *   Every "force update" screen you have seen on iOS is a custom screen plus a
 *   link out to the App Store. So that is what this is.
 *
 * The wall below therefore serves two jobs: it IS the gate on iOS, and it is
 * the BACKSTOP on Android — Play's immediate flow is cancellable, and a user
 * who dismisses it lands here, on a screen whose only button re-invokes it.
 */

import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Capacitor } from '@capacitor/core';
import { AppUpdate } from '@capawesome/capacitor-app-update';

import { useReduced, riseIn, staggerParent } from '../../lib/motion';
import { logWarn } from '../../lib/log';

/** Apple ID from the App Store listing (…/id6760366924). */
const IOS_APP_ID = '6760366924';

export interface ForceUpdateGateProps {
  platform: 'ios' | 'android';
  /** Native version the device is running. Shown as a support footnote. */
  currentVersion: string;
  /** The hard floor it failed. */
  requiredVersion: string;
  /**
   * Override the update action. Production leaves this undefined and uses the
   * native plugin; the Design Lab passes a no-op so the preview does not try to
   * call a native plugin from a browser.
   */
  onUpdate?: () => void;
}

const storeName = (platform: 'ios' | 'android') =>
  platform === 'ios' ? 'the App Store' : 'the Play Store';

/** The brand name is always lowercase, italic, and carries its period. */
const Brand = () => <em>enough.</em>;

export const ForceUpdateGate: React.FC<ForceUpdateGateProps> = ({
  platform,
  currentVersion,
  requiredVersion,
  onUpdate,
}) => {
  const reduced = useReduced();
  const [busy, setBusy] = useState(false);

  const runUpdate = React.useCallback(async () => {
    if (onUpdate) {
      onUpdate();
      return;
    }
    setBusy(true);
    try {
      if (platform === 'android') {
        // Google's native blocking flow. Returns CANCELED if the user backs
        // out — we do nothing special, which leaves them on this wall.
        await AppUpdate.performImmediateUpdate();
      } else {
        await AppUpdate.openAppStore({ appId: IOS_APP_ID });
      }
    } catch (error) {
      logWarn('[forceUpdate] update action failed', error);
    } finally {
      setBusy(false);
    }
  }, [onUpdate, platform]);

  // Android: go straight to Play's native flow. The wall is only what they see
  // if they cancel it.
  useEffect(() => {
    if (onUpdate) return; // Design Lab / tests — never auto-fire.
    if (platform !== 'android' || !Capacitor.isNativePlatform()) return;
    void runUpdate();
  }, [onUpdate, platform, runUpdate]);

  return (
    <div
      className="relative min-h-screen w-full bg-sand font-sans text-growth flex flex-col items-center justify-center px-6"
      style={{ paddingTop: 'max(1.5rem, var(--sat))', paddingBottom: 'max(1.5rem, var(--sab))' }}
    >
      <motion.div
        className="flex flex-col items-center text-center max-w-sm"
        variants={staggerParent(reduced)}
        initial="hidden"
        animate="show"
      >
        <motion.img
          variants={riseIn(reduced)}
          src="/assets/logos/enough_wordmark_green.png"
          alt="enough"
          width={150}
          height={60}
          className="max-w-[150px] h-auto object-contain mb-10"
        />

        <motion.h1 variants={riseIn(reduced)} className="font-serif text-2xl mb-3">
          Time to update
        </motion.h1>

        <motion.p variants={riseIn(reduced)} className="text-growth/70 leading-relaxed mb-8">
          This version of <Brand /> is out of date and can no longer sync your accounts. Update in{' '}
          {storeName(platform)}.
        </motion.p>

        <motion.button
          variants={riseIn(reduced)}
          onClick={() => void runUpdate()}
          disabled={busy}
          className="w-full bg-growth text-cloud rounded-full py-3.5 px-8 font-medium
                     active:scale-[0.98] transition-transform disabled:opacity-60"
        >
          {busy ? 'Opening…' : <>Update <Brand /></>}
        </motion.button>

        <motion.p variants={riseIn(reduced)} className="text-xs text-growth/40 mt-6">
          Version {currentVersion} · {requiredVersion} or later required
        </motion.p>
      </motion.div>
    </div>
  );
};
